import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Check, X, Mail, Edit, Eye, AlertCircle, Lightbulb, Sparkles } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Badge } from '../../../../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../ui/dialog';
import { Alert, AlertDescription } from '../../../../ui/alert';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';
import type { EventLibrary, EventLibraryTemplate, RegionType, EmailTemplateV2, IndustryType } from '../../../../../types/epicengage';
import { format, parseISO } from 'date-fns';

// Monthly campaign ideas - evergreen suggestions for each month
interface MonthlyIdea {
  month: number;
  monthName: string;
  ideas: {
    title: string;
    description: string;
    subjectLine: string;
    industries: IndustryType[];
  }[];
}

const MONTHLY_IDEAS: MonthlyIdea[] = [
  {
    month: 1,
    monthName: 'January',
    ideas: [
      { title: 'New Year, New You', description: 'Kick off the year with fresh starts and resolutions. Offer special deals to help customers achieve their goals.', subjectLine: 'Start 2025 Right – Special Offer Inside!', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Post-Holiday Check-In', description: 'Reconnect with customers after the holiday rush. Show appreciation and share what\'s new.', subjectLine: 'We Missed You! Here\'s What\'s New', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Winter Wellness', description: 'Focus on health and self-care during the cold months. Perfect for wellness tips or comfort products.', subjectLine: 'Stay Cozy & Healthy This Winter', industries: ['WELLNESS', 'HEALTHCARE', 'FNB'] },
    ]
  },
  {
    month: 2,
    monthName: 'February',
    ideas: [
      { title: 'Self-Love Campaign', description: 'Beyond Valentine\'s – encourage customers to treat themselves. Great for spa days, solo dining, or personal treats.', subjectLine: 'You Deserve This – Treat Yourself!', industries: ['WELLNESS', 'FNB', 'RETAIL'] },
      { title: 'Galentine\'s / Palentine\'s', description: 'Celebrate friendships! Offer group deals or refer-a-friend bonuses.', subjectLine: 'Bring Your Bestie – Special Friend Deals', industries: ['FNB', 'WELLNESS', 'RETAIL'] },
      { title: 'Beat the Winter Blues', description: 'Brighten up the month with cheerful offers and mood-boosting products or experiences.', subjectLine: 'Feeling Blue? We\'ve Got You Covered', industries: ['WELLNESS', 'HEALTHCARE', 'FNB', 'RETAIL'] },
    ]
  },
  {
    month: 3,
    monthName: 'March',
    ideas: [
      { title: 'Spring Refresh', description: 'Spring cleaning vibes – new beginnings, fresh products, or updated menus.', subjectLine: 'Fresh for Spring – New Arrivals!', industries: ['FNB', 'RETAIL', 'WELLNESS'] },
      { title: 'Women\'s Month Celebration', description: 'Honor Women\'s History Month with special features or promotions.', subjectLine: 'Celebrating Women All Month Long', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Early Bird Summer Prep', description: 'Get ahead of summer with early booking deals or seasonal products.', subjectLine: 'Summer\'s Coming – Get Ready Now!', industries: ['WELLNESS', 'RETAIL', 'FNB'] },
    ]
  },
  {
    month: 4,
    monthName: 'April',
    ideas: [
      { title: 'Spring Into Savings', description: 'Mid-spring sale or clearance to make room for new seasonal items.', subjectLine: 'Spring Savings Are Here – Up to 30% Off', industries: ['RETAIL', 'FNB', 'WELLNESS'] },
      { title: 'Earth Day / Green Initiative', description: 'Highlight sustainable practices, eco-friendly products, or special green initiatives.', subjectLine: 'Celebrate Earth Day With Us!', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Rainy Day Comfort', description: 'Cozy offerings for April showers – comfort food, indoor activities, or relaxation.', subjectLine: 'Rainy Day? We\'ve Got the Perfect Solution', industries: ['FNB', 'WELLNESS', 'RETAIL'] },
    ]
  },
  {
    month: 5,
    monthName: 'May',
    ideas: [
      { title: 'Mental Health Awareness', description: 'May is Mental Health Awareness Month – share resources, self-care tips, or calming experiences.', subjectLine: 'Taking Care of You – Mental Health Month', industries: ['WELLNESS', 'HEALTHCARE'] },
      { title: 'Pre-Summer Prep', description: 'Last chance to get summer-ready with special packages or products.', subjectLine: 'Summer Is Almost Here – Are You Ready?', industries: ['WELLNESS', 'RETAIL', 'FNB'] },
      { title: 'Graduation Season', description: 'Celebrate graduates with special offers for celebrations and gifts.', subjectLine: 'Congrats, Grads! Celebrate With Us', industries: ['FNB', 'RETAIL'] },
    ]
  },
  {
    month: 6,
    monthName: 'June',
    ideas: [
      { title: 'Summer Kickoff', description: 'Official start of summer! Launch seasonal menus, products, or experiences.', subjectLine: 'Hello Summer! ☀️ New Season, New Vibes', industries: ['FNB', 'RETAIL', 'WELLNESS'] },
      { title: 'Mid-Year Check-In', description: 'Half the year done! Reflect on goals, offer mid-year deals, or loyalty rewards.', subjectLine: 'Halfway There – Special Mid-Year Treats', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Beat the Heat', description: 'Cool treats, refreshing products, or summer wellness tips.', subjectLine: 'Stay Cool This Summer – Hot Deals Inside', industries: ['FNB', 'WELLNESS', 'RETAIL'] },
    ]
  },
  {
    month: 7,
    monthName: 'July',
    ideas: [
      { title: 'Summer Lovin\'', description: 'Peak summer vibes – vacation mode, outdoor experiences, or summer exclusives.', subjectLine: 'Summer Is in Full Swing – Don\'t Miss Out!', industries: ['FNB', 'RETAIL', 'WELLNESS'] },
      { title: 'Staycation Special', description: 'For those not traveling – local experiences, at-home treats, or self-care packages.', subjectLine: 'Staycation Mode – Treat Yourself at Home', industries: ['FNB', 'WELLNESS', 'RETAIL'] },
      { title: 'Christmas in July', description: 'Fun mid-year sale event with holiday-level savings.', subjectLine: '🎄 Christmas in July Sale – Huge Savings!', industries: ['RETAIL'] },
    ]
  },
  {
    month: 8,
    monthName: 'August',
    ideas: [
      { title: 'Back-to-Routine', description: 'Kids going back to school, adults back to routines – help ease the transition.', subjectLine: 'Back to Routine? We\'re Here to Help', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Last Days of Summer', description: 'End-of-summer celebrations and final chances for seasonal offerings.', subjectLine: 'Summer\'s Almost Over – Last Chance!', industries: ['FNB', 'RETAIL', 'WELLNESS'] },
      { title: 'Self-Improvement Month', description: 'New habits, new goals – encourage wellness and personal growth.', subjectLine: 'Level Up This Month – Start Something New', industries: ['WELLNESS', 'HEALTHCARE'] },
    ]
  },
  {
    month: 9,
    monthName: 'September',
    ideas: [
      { title: 'Fall Into Savings', description: 'Welcome fall with seasonal promotions and autumn-themed offerings.', subjectLine: 'Fall Is Here – Cozy Deals Await!', industries: ['FNB', 'RETAIL', 'WELLNESS'] },
      { title: 'New Season, New You', description: 'Fresh start energy as summer ends – new products, services, or routines.', subjectLine: 'Fresh Start This Fall – What\'s New', industries: ['WELLNESS', 'RETAIL', 'HEALTHCARE'] },
      { title: 'Customer Appreciation', description: 'Show gratitude with exclusive deals for loyal customers.', subjectLine: 'Thank You! Exclusive Rewards Inside', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
    ]
  },
  {
    month: 10,
    monthName: 'October',
    ideas: [
      { title: 'Spooky Season Fun', description: 'Halloween vibes – themed products, events, or festive offerings.', subjectLine: '👻 Spooky Good Deals – Limited Time!', industries: ['FNB', 'RETAIL'] },
      { title: 'Fall Wellness', description: 'Immune boosting, cozy self-care, and autumn wellness tips.', subjectLine: 'Stay Healthy This Fall – Wellness Tips', industries: ['WELLNESS', 'HEALTHCARE', 'FNB'] },
      { title: 'Breast Cancer Awareness', description: 'October is Breast Cancer Awareness Month – show support and share resources.', subjectLine: 'Together for a Cause – Pink October', industries: ['HEALTHCARE', 'WELLNESS', 'RETAIL'] },
    ]
  },
  {
    month: 11,
    monthName: 'November',
    ideas: [
      { title: 'Gratitude Month', description: 'Thanksgiving vibes all month – thank customers and share appreciation.', subjectLine: 'Grateful for You – A Special Thank You', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Holiday Gift Guide', description: 'Help customers plan ahead with gift ideas and recommendations.', subjectLine: 'Your Holiday Gift Guide Is Here!', industries: ['RETAIL', 'FNB', 'WELLNESS'] },
      { title: 'Early Holiday Deals', description: 'Beat the rush with early bird holiday promotions.', subjectLine: 'Early Bird Holiday Deals – Shop Now!', industries: ['RETAIL', 'FNB'] },
    ]
  },
  {
    month: 12,
    monthName: 'December',
    ideas: [
      { title: 'Holiday Countdown', description: 'Advent-style daily deals or countdown to Christmas excitement.', subjectLine: '🎁 12 Days of Deals – Day 1!', industries: ['RETAIL', 'FNB'] },
      { title: 'Year in Review', description: 'Celebrate the year\'s milestones with customers. Share achievements and thank them.', subjectLine: 'What a Year! 2024 Highlights', industries: ['FNB', 'RETAIL', 'WELLNESS', 'HEALTHCARE'] },
      { title: 'Last-Minute Gifting', description: 'Gift cards, quick delivery options, or instant experiences for procrastinators.', subjectLine: 'Last-Minute Gift Ideas – We\'ve Got You!', industries: ['RETAIL', 'FNB', 'WELLNESS'] },
    ]
  },
];

interface EventWithTemplates extends EventLibrary {
  templates: EventLibraryTemplate[];
  isEnabled: boolean;
  merchantTemplates?: EmailTemplateV2[];
}

export default function Events() {
  const navigate = useNavigate();
  const { merchant } = useMerchant();
  const [events, setEvents] = useState<EventWithTemplates[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<EventWithTemplates | null>(null);
  const [previewTemplates, setPreviewTemplates] = useState<EventLibraryTemplate[]>([]);
  const [merchantEventTemplates, setMerchantEventTemplates] = useState<EmailTemplateV2[]>([]);
  const [activeRegion, setActiveRegion] = useState<RegionType | 'ALL'>('ALL');
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchEvents = async () => {
    if (!merchant) return;

    // Fetch all events
    const { data: eventsData, error: eventsError } = await epicengageSupabase
      .from('event_library')
      .select('*')
      .order('event_date');

    if (eventsError) {
      console.error('Error fetching events:', eventsError);
      return;
    }

    // Fetch templates for merchant's industry
    const { data: templatesData } = await epicengageSupabase
      .from('event_library_templates')
      .select('*')
      .eq('industry', merchant.industry);

    // Fetch enabled events for this merchant
    const { data: enabledData } = await epicengageSupabase
      .from('enabled_events')
      .select('*')
      .eq('merchant_id', merchant.id);

    // Fetch merchant's event templates
    const { data: merchantTemplatesData } = await epicengageSupabase
      .from('email_templates_v2')
      .select('*')
      .eq('merchant_id', merchant.id)
      .eq('type', 'EVENT');

    const enabledMap = new Map(enabledData?.map(e => [e.event_library_id, e.enabled]) || []);

    const mapped = (eventsData || []).map(event => ({
      ...event,
      templates: templatesData?.filter(t => t.event_library_id === event.id) || [],
      isEnabled: enabledMap.get(event.id) || false,
      merchantTemplates: (merchantTemplatesData || []).filter(t => 
        t.name.includes(event.name) || t.origin === 'EVENT_LIBRARY'
      ) as EmailTemplateV2[]
    }));

    setEvents(mapped);
    setLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, [merchant]);

  const toggleEvent = async (event: EventWithTemplates) => {
    if (!merchant) return;

    if (event.isEnabled) {
      // Disable
      const { error } = await epicengageSupabase
        .from('enabled_events')
        .update({ enabled: false })
        .eq('merchant_id', merchant.id)
        .eq('event_library_id', event.id);

      if (error) {
        toast.error('Failed to disable event');
        return;
      }
      toast.success('Event disabled');
    } else {
      // Enable (upsert) + create merchant templates if not exist
      const { error } = await epicengageSupabase
        .from('enabled_events')
        .upsert({
          merchant_id: merchant.id,
          event_library_id: event.id,
          enabled: true
        }, { onConflict: 'merchant_id,event_library_id' });

      if (error) {
        toast.error('Failed to enable event');
        return;
      }

      // Create merchant-specific templates for this event if they don't exist
      const existingTemplates = await epicengageSupabase
        .from('email_templates_v2')
        .select('name')
        .eq('merchant_id', merchant.id)
        .eq('type', 'EVENT')
        .like('name', `%${event.name}%`);

      if ((existingTemplates.data?.length || 0) === 0 && event.templates.length > 0) {
        // Create templates from event library
        const newTemplates = event.templates.map(t => ({
          merchant_id: merchant.id,
          name: `${event.name} - Email ${t.step_order}`,
          type: 'EVENT' as const,
          origin: 'EVENT_LIBRARY',
          editor_type: 'STRUCTURED' as const,
          subject: t.subject,
          body_json: {
            greeting: `Hi {{first_name}}!`,
            content: t.body_seed,
            cta_text: t.default_cta_label || 'Learn More',
            cta_url: t.default_cta_url_hint || 'https://example.com',
            footer: 'Best regards,\nThe Team'
          }
        }));

        await epicengageSupabase.from('email_templates_v2').insert(newTemplates);
      }

      toast.success('Event enabled! Emails will be scheduled automatically.');
    }

    fetchEvents();
  };

  const openPreview = async (event: EventWithTemplates) => {
    if (!merchant) return;
    
    // Fetch library templates
    const { data: libraryTemplates } = await epicengageSupabase
      .from('event_library_templates')
      .select('*')
      .eq('event_library_id', event.id)
      .eq('industry', merchant.industry)
      .order('step_order');

    // Fetch merchant's templates for this event
    const { data: merchantTemplates } = await epicengageSupabase
      .from('email_templates_v2')
      .select('*')
      .eq('merchant_id', merchant.id)
      .eq('type', 'EVENT')
      .like('name', `%${event.name}%`);

    setSelectedEvent(event);
    setPreviewTemplates(libraryTemplates || []);
    setMerchantEventTemplates((merchantTemplates || []) as EmailTemplateV2[]);
    setPreviewOpen(true);
  };

  const handleEditEmail = (event: EventWithTemplates, stepOrder: number) => {
    // Find the merchant template for this step
    const merchantTemplate = merchantEventTemplates.find(t => 
      t.name.includes(`Email ${stepOrder}`) || t.name.includes(`Step ${stepOrder}`)
    );

    if (merchantTemplate) {
      // Navigate to edit the existing merchant template
      navigate(`/clientpanel/enage/templates/${merchantTemplate.id}/edit`);
    } else {
      // Create new template and navigate to it
      createAndEditTemplate(event, stepOrder);
    }
    setPreviewOpen(false);
  };

  const createAndEditTemplate = async (event: EventWithTemplates, stepOrder: number) => {
    if (!merchant) return;

    const libraryTemplate = previewTemplates.find(t => t.step_order === stepOrder);
    if (!libraryTemplate) return;

    const { data, error } = await epicengageSupabase
      .from('email_templates_v2')
      .insert({
        merchant_id: merchant.id,
        name: `${event.name} - Email ${stepOrder}`,
        type: 'EVENT',
        origin: 'EVENT_LIBRARY',
        editor_type: 'STRUCTURED',
        subject: libraryTemplate.subject,
        body_json: {
          greeting: `Hi {{first_name}}!`,
          content: libraryTemplate.body_seed,
          cta_text: libraryTemplate.default_cta_label || 'Learn More',
          cta_url: libraryTemplate.default_cta_url_hint || 'https://example.com',
          footer: 'Best regards,\nThe Team'
        }
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to create template');
      return;
    }

    navigate(`/clientpanel/enage/templates/${data.id}/edit`);
  };

  const getRegionIcon = (region: RegionType) => {
    switch (region) {
      case 'SG': return '🇸🇬';
      case 'US': return '🇺🇸';
      case 'GLOBAL': return '🌍';
    }
  };

  const filteredEvents = activeRegion === 'ALL' 
    ? events 
    : events.filter(e => e.region === activeRegion || e.region === 'GLOBAL');

  // Get current month's ideas and next month's
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const currentMonthIdeas = MONTHLY_IDEAS.find(m => m.month === currentMonth);
  const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
  const nextMonthIdeas = MONTHLY_IDEAS.find(m => m.month === nextMonth);

  // Filter ideas by merchant industry
  const filterIdeasByIndustry = (ideas: MonthlyIdea['ideas']) => {
    if (!merchant?.industry) return ideas;
    return ideas.filter(idea => idea.industries.includes(merchant.industry));
  };

  if (loading) {
    return (
      <V1Layout>
        <div className="p-8 text-muted-foreground">Loading...</div>
      </V1Layout>
    );
  }

  return (
    <V1Layout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Calendar className="h-8 w-8" />
            Events & Campaigns
          </h1>
          <p className="text-muted-foreground mt-1">
            Enable holiday campaigns and get monthly campaign ideas
          </p>
        </div>

        <Tabs defaultValue="events" className="space-y-6">
          <TabsList>
            <TabsTrigger value="events">
              <Calendar className="h-4 w-4 mr-2" />
              Holiday Events
            </TabsTrigger>
            <TabsTrigger value="ideas">
              <Lightbulb className="h-4 w-4 mr-2" />
              Monthly Ideas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="space-y-6">
            {/* Region Filter */}
            <div className="flex gap-2">
              <Button
                variant={activeRegion === 'ALL' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveRegion('ALL')}
              >
                All Regions
              </Button>
              <Button
                variant={activeRegion === 'SG' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveRegion('SG')}
              >
                🇸🇬 Singapore
              </Button>
              <Button
                variant={activeRegion === 'US' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveRegion('US')}
              >
                🇺🇸 USA
              </Button>
              <Button
                variant={activeRegion === 'GLOBAL' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveRegion('GLOBAL')}
              >
                🌍 Global
              </Button>
            </div>

            {/* Events Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredEvents.map(event => {
                const eventDate = parseISO(event.event_date);
                const isUpcoming = eventDate > new Date();
                const daysUntil = Math.ceil((eventDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                
                return (
                  <Card key={event.id} className={event.isEnabled ? 'border-primary' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {getRegionIcon(event.region)}
                            {event.name}
                          </CardTitle>
                          <CardDescription>
                            {format(eventDate, 'MMMM d, yyyy')}
                            {isUpcoming && daysUntil <= 30 && (
                              <Badge variant="secondary" className="ml-2">
                                {daysUntil} days
                              </Badge>
                            )}
                          </CardDescription>
                        </div>
                        <Button
                          variant={event.isEnabled ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleEvent(event)}
                        >
                          {event.isEnabled ? (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Enabled
                            </>
                          ) : (
                            'Enable'
                          )}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        {event.description || `Send automated emails for ${event.name}`}
                      </p>
                      {event.templates.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span>{event.templates.length} email{event.templates.length !== 1 ? 's' : ''}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={() => openPreview(event)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredEvents.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No events found for the selected region.
              </div>
            )}
          </TabsContent>

          <TabsContent value="ideas" className="space-y-6">
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                These are evergreen campaign ideas tailored for your industry. Use them as inspiration for manual blasts anytime!
              </AlertDescription>
            </Alert>

            {/* Current Month */}
            {currentMonthIdeas && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">📅</span>
                    {currentMonthIdeas.monthName} Ideas
                    <Badge>This Month</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {filterIdeasByIndustry(currentMonthIdeas.ideas).map((idea, idx) => (
                    <div key={idx} className="p-4 bg-muted rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{idea.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{idea.description}</p>
                          <p className="text-xs mt-2">
                            <span className="font-medium">Suggested subject:</span> {idea.subjectLine}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/clientpanel/enage/blast')}
                        >
                          Use Idea
                        </Button>
                      </div>
                    </div>
                  ))}
                  {filterIdeasByIndustry(currentMonthIdeas.ideas).length === 0 && (
                    <p className="text-muted-foreground text-sm">No ideas match your industry for this month.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Next Month */}
            {nextMonthIdeas && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">🔜</span>
                    {nextMonthIdeas.monthName} Ideas
                    <Badge variant="outline">Coming Up</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {filterIdeasByIndustry(nextMonthIdeas.ideas).map((idea, idx) => (
                    <div key={idx} className="p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{idea.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{idea.description}</p>
                          <p className="text-xs mt-2">
                            <span className="font-medium">Suggested subject:</span> {idea.subjectLine}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filterIdeasByIndustry(nextMonthIdeas.ideas).length === 0 && (
                    <p className="text-muted-foreground text-sm">No ideas match your industry for next month.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Email Preview Modal */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedEvent?.name} Emails</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {previewTemplates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No email templates available for your industry.</p>
                  <p className="text-sm">Contact support to add templates for this event.</p>
                </div>
              ) : (
                previewTemplates.map((template) => {
                  const merchantTemplate = merchantEventTemplates.find(t =>
                    t.name.includes(`Email ${template.step_order}`) || t.name.includes(`Step ${template.step_order}`)
                  );
                  const hasCustomTemplate = !!merchantTemplate;

                  return (
                    <Card key={template.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">
                                {template.send_offset_days === 0
                                  ? 'Day of event'
                                  : template.send_offset_days < 0
                                    ? `${Math.abs(template.send_offset_days)} days before`
                                    : `${template.send_offset_days} days after`}
                              </Badge>
                              {hasCustomTemplate && (
                                <Badge variant="secondary">Customized</Badge>
                              )}
                            </div>
                            <h4 className="font-semibold">{template.subject}</h4>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {template.body_seed}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => selectedEvent && handleEditEmail(selectedEvent, template.step_order)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            {hasCustomTemplate ? 'Edit' : 'Customize'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </V1Layout>
  );
}
